import { useState } from "react";
import { containsMaskedAndUnmaskedAliases } from "../utilities/select-alias-utilities";
import { type SelectedRecoveryAlias } from "../views/select-alias/username-recovery-select-alias-view-types";
import { useSelectAliasFilteredAccounts } from "./use-select-alias-filtered-accounts";
import { useSelectAliasHandlers } from "./use-select-alias-handlers";
import { useSelectAliasStrings } from "./use-select-alias-strings";

/**
 * Custom hook for select alias for username recovery.
 * @returns handler for calling useSelectAlias.
 */
export const useSelectAlias = function useSelectAlias() {
  const [selectedAlias, setSelectedAlias] = useState<SelectedRecoveryAlias | undefined>();

  const { filteredAccountsForProofResult, isMultipleAliases, radioOptions } =
    useSelectAliasFilteredAccounts(selectedAlias);

  const isGenericServiceError = containsMaskedAndUnmaskedAliases(filteredAccountsForProofResult);
  const noAliases = !filteredAccountsForProofResult.aliases.length;
  const showAliases = !(noAliases || isGenericServiceError);

  const { usernameRecoveryTitle, usernameRecoveryDescription, primaryButtonText } =
    useSelectAliasStrings({
      isGenericServiceError,
      filteredAccountsForProofResult,
      isMultipleAliases,
    });

  const {
    aliasInputValue,
    error,
    onSubmitHandler,
    onCancelHandler,
    handleAliasSelection,
    handleAliasInputChange,
  } = useSelectAliasHandlers(
    filteredAccountsForProofResult,
    selectedAlias,
    setSelectedAlias,
    isGenericServiceError,
  );

  // only disable primary button if there is no error and there are multiple aliases and no alias is selected
  const disablePrimaryButton = !isGenericServiceError && isMultipleAliases && !selectedAlias;

  return {
    onSubmitHandler,
    onCancelHandler,
    filteredProofState: filteredAccountsForProofResult,
    handleAliasSelection,
    selectedAlias,
    handleAliasInputChange,
    aliasInputValue,
    error,
    showAliases,
    usernameRecoveryDescription,
    usernameRecoveryTitle,
    isMultipleAliases,
    radioOptions,
    disablePrimaryButton,
    primaryButtonText,
  };
};
